// src/components/LandingPage/Navbar/NavbarMobile.tsx
import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { supportedLanguages, languageFlags } from '../../../constants/languageFlags'
import logo from '../../../assets/damelio_logo_blanco.png'
import logoText from '../../../assets/damelio_texto_blanco.png'

interface NavbarMobileProps {
  fixedTop: number          // ⇒ lo controla Layout
  hideScan?: boolean        // ⇒ true sólo en /scan
}

export default function NavbarMobile ({
  fixedTop,
  hideScan = false,
}: NavbarMobileProps) {
  const { t, i18n }   = useTranslation()
  const location      = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [showLang, setShowLang] = useState(false)
  const langRef       = useRef<HTMLDivElement>(null)

  const isActive = (path: string) => location.pathname === path

  /* cerrar dropdown idioma si se hace click fuera */
  useEffect(() => {
    function clickOutside (e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setShowLang(false)
      }
    }
    if (showLang) document.addEventListener('mousedown', clickOutside)
    return () => document.removeEventListener('mousedown', clickOutside)
  }, [showLang])

  return (
    <nav
      className="fixed inset-x-0 z-50 bg-[#10172A] border-b border-secondary/50
                 shadow-md text-white"
      style={{ top: fixedTop }}
    >
      {/* ───────── barra superior ───────── */}
      <div className="flex items-center h-20 px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2"
              onClick={() => setMenuOpen(false)}>
          <img src={logo}      alt="logo"    className="h-12 w-12" />
          <img src={logoText}  alt="Damelio" className="h-10" />
        </Link>

        {/* Selector idioma + hamburguesa */}
        <div className="flex items-center gap-3 ml-auto" ref={langRef}>
          {/* idioma */}
          <button
            onClick={() => setShowLang(!showLang)}
            className="flex items-center bg-gray-800 p-1 rounded-full"
            aria-label={t('nav.language')}
          >
            <img src={languageFlags[i18n.language]} alt={i18n.language}
                 className="w-6 h-6 rounded-full" />
            <ChevronDown className="ml-1 h-4 w-4" />
          </button>

          {/* hamburguesa */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="z-10">
            {menuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>

          {/* dropdown idioma */}
          {showLang && (
            <div className="absolute right-0 top-14 w-44 bg-gray-900
                            border border-gray-700 rounded-lg shadow-lg py-2 z-50">
              {supportedLanguages.map(({ code, nameKey }) => (
                <button
                  key={code}
                  onClick={() => {
                    i18n.changeLanguage(code)   // ← funciona como antes
                    setShowLang(false)
                  }}
                  className={`flex items-center w-full px-4 py-2 hover:bg-gray-800 transition
                             ${i18n.language === code
                                 ? 'font-bold text-primary'
                                 : 'text-gray-300'}`}
                >
                  <img src={languageFlags[code]} alt={code}
                       className="w-5 h-5 mr-2 rounded" />
                  {t(`settings.language.${nameKey}`)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ───────── menú desplegable ───────── */}
      {menuOpen && (
        <div className="bg-[#10172A] px-6 pb-6 flex flex-col space-y-4">
          {[
            { to: '/',              label: t('nav.home')           },
            { to: '/documentation', label: t('nav.what_we_find')   },
            { to: '/partners',      label: t('nav.partners')       },
            { to: '/faq',           label: t('nav.faq')            },
          ].map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              onClick={() => setMenuOpen(false)}
              className={`relative text-lg font-semibold ${
                isActive(to) ? 'text-white' : 'text-gray-300 hover:text-primary group'
              }`}
            >
              {label}
              <span className={`absolute bottom-0 left-0 h-0.5 bg-primary
                                 transition-transform duration-300 origin-left
                                 ${isActive(to)
                                     ? 'w-full'
                                     : 'w-full scale-x-0 group-hover:scale-x-100'}`} />
            </Link>
          ))}

          {/* Botón Escanear sólo si hideScan es false */}
          {!hideScan && (
            <Link to="/scan"
                  onClick={() => setMenuOpen(false)}
                  className="bg-green-600 rounded-full px-4 py-2 text-center">
              {t('nav.scan')}
            </Link>
          )}

          {/* Botón Entrar */}
          <Link to="/login"
                onClick={() => setMenuOpen(false)}
                className="bg-primary rounded-full px-4 py-2 text-center">
            {t('nav.login')}
          </Link>
        </div>
      )}
    </nav>
  )
}
